import React, { useState } from 'react';
import { logService } from '../../services/logService';
import type { Log, LogStatus } from '../../types/log';
import ClinicLogDetails from './ClinicLogDetails';
import { generatePlaybackUrl, navigateToPlayback } from '../../services/playbackUrl';

interface LogDetailProps {
  log: Log;
  onClose: () => void;
  onEdit: () => void;
}

const LogDetail: React.FC<LogDetailProps> = ({ log, onClose, onEdit }) => {
  const [isUpdating, setIsUpdating] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const handleStatusChange = async (status: LogStatus) => {
    setIsUpdating(true);
    try {
      await logService.updateLog(log.id, { status });
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDeleteClick = () => {
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    setShowDeleteConfirm(false);
    if (!log?.id) {
      console.error('Log ID is missing');
      return;
    }
    setIsUpdating(true);
    try {
      await logService.deleteLog(log.id);
      onClose();
    } catch (err) {
      console.error('Failed to delete log:', err);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirm(false);
  };

  const getPriorityColor = (priority: number) => {
    if (priority >= 3) return 'text-red-500 bg-red-500/10';
    if (priority >= 2) return 'text-yellow-500 bg-yellow-500/10';
    if (priority >= 1) return 'text-green-500 bg-green-500/10';
    return 'text-gray-500 bg-gray-500/10';
  };

  const formatTimestamp = (timestamp: number | undefined) => {
    if (!timestamp) return 'Not set';
    const date = new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatDateTime = (timestamp: number) => {
    const date = new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="p-4">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold dark:text-claude-darkText text-claude-text">
          Log Details
        </h2>
        <button
          type="button"
          onClick={onClose}
          className="p-1.5 rounded-lg hover:dark:bg-claude-darkSurfaceHover hover:bg-claude-surfaceHover transition-colors"
        >
          <svg
            className="h-5 w-5 dark:text-claude-darkTextSecondary text-claude-textSecondary"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <div className="space-y-4">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getPriorityColor(log.priority)}`}>
              Priority {log.priority}
            </span>
            <span className="px-2 py-0.5 rounded-full text-xs font-medium capitalize bg-claude-accent/10 text-claude-accent">
              {log.category}
            </span>
          </div>
          <h3 className={`text-lg font-medium dark:text-claude-darkText text-claude-text ${log.status === 'completed' ? 'line-through opacity-60' : ''}`}>
            {log.title}
          </h3>
        </div>

        {log.startTime && (() => {
          const playbackUrl = generatePlaybackUrl(log);
          return playbackUrl ? (
            <div className="mt-2 p-2.5 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800/40">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <svg className="w-4 h-4 text-blue-500 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className="text-sm font-medium text-blue-700 dark:text-blue-300">
                    Dashboard Playback
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => navigateToPlayback(log)}
                    className="px-3 py-1 text-xs font-medium rounded-md bg-blue-500 hover:bg-blue-600 text-white transition-colors flex items-center gap-1.5"
                  >
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Open Playback
                  </button>
                  <button
                    type="button"
                    onClick={() => { navigator.clipboard.writeText(playbackUrl); }}
                    className="px-2 py-1 text-xs font-medium rounded-md border border-blue-300 dark:border-blue-700 text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors"
                    title="Copy playback link"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          ) : null;
        })()}

        {log.description && log.category !== 'clinic_clinical_log' && (
          <div>
            <label className="text-xs font-medium dark:text-claude-darkTextSecondary text-claude-textSecondary uppercase tracking-wider">
              Description
            </label>
            <pre className="mt-1 text-sm dark:text-claude-darkText text-claude-text whitespace-pre-wrap font-sans">
              {log.description}
            </pre>
          </div>
        )}

        {log.category === 'clinic_clinical_log' && (
          <div>
            <label className="text-xs font-medium dark:text-claude-darkTextSecondary text-claude-textSecondary uppercase tracking-wider">
              Clinical Record
            </label>
            <div className="mt-1">
              <ClinicLogDetails log={log} />
            </div>
          </div>
        )}

        {log.startTime && (
          <div>
            <label className="text-xs font-medium dark:text-claude-darkTextSecondary text-claude-textSecondary uppercase tracking-wider">
              Start Time
            </label>
            <p className="mt-1 text-sm dark:text-claude-darkText text-claude-text">
              {formatTimestamp(log.startTime)}
            </p>
          </div>
        )}

        {log.endTime && (
          <div>
            <label className="text-xs font-medium dark:text-claude-darkTextSecondary text-claude-textSecondary uppercase tracking-wider">
              End Time
            </label>
            <p className="mt-1 text-sm dark:text-claude-darkText text-claude-text">
              {formatTimestamp(log.endTime)}
            </p>
          </div>
        )}

        <div>
          <label className="text-xs font-medium dark:text-claude-darkTextSecondary text-claude-textSecondary uppercase tracking-wider">
            Status
          </label>
          <div className="mt-2 flex gap-2">
            {log.status !== 'active' && (
              <button
                type="button"
                onClick={() => handleStatusChange('active')}
                disabled={isUpdating}
                className="px-3 py-1.5 text-xs font-medium rounded-lg border border-blue-500/20 bg-blue-500/10 text-blue-500 hover:bg-blue-500/20 transition-colors disabled:opacity-50"
              >
                Mark Active
              </button>
            )}
            {log.status !== 'completed' && (
              <button
                type="button"
                onClick={() => handleStatusChange('completed')}
                disabled={isUpdating}
                className="px-3 py-1.5 text-xs font-medium rounded-lg border border-green-500/20 bg-green-500/10 text-green-500 hover:bg-green-500/20 transition-colors disabled:opacity-50"
              >
                Complete
              </button>
            )}
            {log.status !== 'archived' && (
              <button
                type="button"
                onClick={() => handleStatusChange('archived')}
                disabled={isUpdating}
                className="px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-500/20 hover:dark:bg-claude-darkSurfaceHover hover:bg-claude-surfaceHover transition-colors disabled:opacity-50"
              >
                Archive
              </button>
            )}
          </div>
        </div>

        <div className="pt-4 border-t dark:border-claude-darkBorder border-claude-border">
          <div className="text-xs dark:text-claude-darkTextSecondary text-claude-textSecondary space-y-1">
            <p>Created: {formatDateTime(log.createdAt)}</p>
            <p>Updated: {formatDateTime(log.updatedAt)}</p>
          </div>
        </div>

        <div className="flex gap-2 pt-4">
          <button
            type="button"
            onClick={onEdit}
            disabled={isUpdating}
            className="flex-1 px-3 py-2 text-sm font-medium rounded-lg border dark:border-claude-darkBorder border-claude-border hover:dark:bg-claude-darkSurfaceHover hover:bg-claude-surfaceHover transition-colors disabled:opacity-50"
          >
            Edit
          </button>
          <button
            type="button"
            onClick={handleDeleteClick}
            disabled={isUpdating}
            className="px-3 py-2 text-sm font-medium rounded-lg border border-red-500/20 text-red-500 hover:bg-red-500/10 transition-colors disabled:opacity-50"
          >
            Delete
          </button>
        </div>

        {showDeleteConfirm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="absolute inset-0 bg-black/50" onClick={handleDeleteCancel} />
            <div className="relative w-full max-w-sm mx-4 bg-white dark:bg-claude-darkSurface rounded-xl shadow-xl p-6">
              <h3 className="text-lg font-semibold dark:text-claude-darkText text-claude-text mb-2">
                Delete Log
              </h3>
              <p className="dark:text-claude-darkTextSecondary text-claude-textSecondary mb-6">
                Are you sure you want to delete "{log.title}"? This action cannot be undone.
              </p>
              <div className="flex gap-3 justify-end">
                <button
                  type="button"
                  onClick={handleDeleteCancel}
                  className="px-4 py-2 text-sm font-medium rounded-lg border dark:border-claude-darkBorder border-claude-border hover:dark:bg-claude-darkSurfaceHover hover:bg-claude-surfaceHover transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleDeleteConfirm}
                  disabled={isUpdating}
                  className="px-4 py-2 text-sm font-medium rounded-lg bg-red-500 text-white hover:bg-red-600 transition-colors disabled:opacity-50"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LogDetail;